/* eslint-disable */
import React, { useEffect } from 'react';
import Status from '@ingka/status';
import { navigate } from 'gatsby';
import styled from 'styled-components';
import variables from '@ingka/variables/design-tokens';
import cssIngka from '@ingka/variables/colours-css';
import translatePage from '../i18n/translatePage';
import useTranslation from '../hooks/useTranslation';
import Loading, { LoadingBall } from '@ingka/loading';
import { Divider } from '../styles';
import { Item } from '../components/Item';
import SiteMetadata from '../components/SiteMetadata';
import { getURLParameters } from '../helpers/url';
import { StatusType } from '../interfaces/Cart';
import { useSummary } from '../hooks/useSummary';
import TMarkUp from '../components/TMarkUp';
import Breadcrumbs from '../components/Breadcrumbs';

const StyledInformation = styled.div`
  margin: 0.5rem 0 1rem;
`;

const DetailsSection = styled.div`
  padding: 1.5rem 0;

  h3 {
    margin-bottom: 1.25rem;
  }
`;

const SubTotals = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 1.5rem 0;
`;

const Total = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 1.5rem 0;
  font-weight: ${variables.fontWeightBold};
  span:last-child {
    font-size: 1.25rem;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 30px 0;
  flex-direction: column;
  ${`@media (min-width: ${variables.breakpointM}) {
    flex-direction: row;
    margin: 90px 0;
  }`}
`;

const SummaryLeftCol = styled.div`
  width: 100%;
  ${`@media (min-width: ${variables.breakpointM}) {
    width: 30%;
  }`}
`;

const SummaryRightCol = styled.div`
  width: 100%;
  ${`@media (min-width: ${variables.breakpointM}) {
    margin-top: 20px;
    width: 70%;
  }`}
`;

const Wrapper = styled.div`
  ${`@media (min-width: ${variables.breakpointM}) {
    max-width: 256px;
    margin: 0 auto 0 0;
  }`}
`;

const SummaryDivider = styled.div`
  ${`@media (min-width: ${variables.breakpointM}) {
    border-right: 1px solid ${cssIngka.colourNeutral3};
    margin: 0 105px;
  }`}
`;

const DeliveryStatusText = styled.h2.attrs({ as: 'h2', className: 'h2' })`
  display: flex;
`;

const StyledStatus = styled(Status)`
  margin-inline-start: 8px;
`;

const DeliveryStatus = styled.div`
  display: flex;
`;
interface StatusProps {
  status: StatusType;
}
const StatusColor: React.FC<StatusProps> = ({ status }): JSX.Element => {
  const statusColor = status === 'CANCELLED' ? 'orange' : 'green';
  const { t } = useTranslation();
  return (
    <StyledStatus
      statusColor={statusColor}
      label={t(`spareParts.summary.status.${status}`)}
      statusDotPosition="trailing"
    />
  );
};

const StatusComponent = (): JSX.Element | null => {
  const { t } = useTranslation();
  const crumbs = [{ url: '/status', text: t('seoContent.page.status') }];
  const title = t('seoContent.page.summary');

  const { status, items, loading } = useSummary(
    getURLParameters('order') as string,
    getURLParameters('token') as string
  );

  useEffect(() => {
    if (!loading) {
      if (items.length === 0) {
        navigate('/');
      } else if (typeof window !== 'undefined') {
        window.history.replaceState('', '', window.location.pathname);
      }
    }
  }, [items, loading]);

  if (loading) {
    return (
      <Loading
        text={t('loading', {
          loading: t(`spareParts.sparePart`),
        })}
      >
        <LoadingBall />
      </Loading>
    );
  }
  if (!items.length) {
    return null;
  }

  const nbrOfItemsText =
    items.length === 1
      ? t('spareParts.summary.items.singular')
      : t('spareParts.summary.items.plural');

  return (
    <>
      <SiteMetadata pageTitle={title} />
      <Breadcrumbs crumbs={crumbs} />
      <FlexContainer>
        <SummaryLeftCol>
          <Wrapper>
            <h1 className="h1">
              <TMarkUp value={t('spareParts.summary.title.highlighted')} />
              <br />
              <TMarkUp value={t('spareParts.summary.title.normal')} />
            </h1>
            <StyledInformation>
              <p>
                <TMarkUp
                  value={t('spareParts.summary.shipping.deliveryTime')}
                />
              </p>
            </StyledInformation>
          </Wrapper>
        </SummaryLeftCol>
        <SummaryDivider />
        <SummaryRightCol>
          <DetailsSection>
            <DeliveryStatus>
              <DeliveryStatusText>
                <TMarkUp
                  value={`${t('spareParts.summary.delivery.status')}:`}
                />
                <StatusColor status={status} />
              </DeliveryStatusText>
            </DeliveryStatus>
          </DetailsSection>
          <DetailsSection>
            <h3 className="h4">{nbrOfItemsText}</h3>
            <ul>
              {items.map((sparePartItem) => {
                const { item, quantity } = sparePartItem;
                return (
                  <Item item={item} key={item.id} showImageDot={true}>
                    <TMarkUp
                      value={t('spareParts.summary.items.quantity', {
                        quantity,
                      })}
                    />
                  </Item>
                );
              })}
            </ul>
          </DetailsSection>
          <Divider />
          <SubTotals>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.subtotal')} />
            </span>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.free')} />
            </span>
          </SubTotals>
          <SubTotals>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.shippingCost')} />
            </span>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.free')} />
            </span>
          </SubTotals>
          <Divider thick />
          <Total>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.total')} />
            </span>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.free')} />
            </span>
          </Total>
        </SummaryRightCol>
      </FlexContainer>
    </>
  );
};

export default translatePage()(StatusComponent);
